import React, { useState, useEffect, useRef } from "react";
import BaseMasterGrid from "../BaseMasterGrid";
import axios from "axios";
import { BASE_URL } from "../../../constants";
import KendoLoader from "../../KendoLoader";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { debounce } from "lodash";
import { toast, ToastContainer } from "react-toastify";

// // Axios interceptor for JWT token
// axios.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem("authToken");
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => Promise.reject(error)
// );

const ExchangeRateMaster = () => {
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [pageState, setPageState] = useState({ take: 10, skip: 0, total: 0, value: "~ALL" });
  const [baseCurrencies, setBaseCurrencies] = useState([]);
  const [filteredBaseCurrencies, setFilteredBaseCurrencies] = useState([]);
  const initialBaseCurrency = { code: "INR", name: "Indian Rupee" }; // Default currency object
  const [selectedBaseCurrency, setSelectedBaseCurrency] = useState(initialBaseCurrency);
  const currentDate = new Date();
  const initialDate = new Date(currentDate);
  initialDate.setDate(currentDate.getDate() - 1);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [isResetting, setIsResetting] = useState(false);
  const isLoadingRef = useRef(false);

  const gridColumns = [
    { field: "currencyCode", title: "Currency Code", filterable: true, sortable: true, width: "200px" },
    { field: "currencyName", title: "Currency Name", filterable: true, sortable: true },
    { field: "value", title: "Exchange Rate", format: "{0:n4}" },
    { field: "date", title: "Last Updated", width: "200px" },
  ];

  // Format date to YYYY-MM-DD for API
  const formatApiDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Load base currencies for dropdown
  const loadBaseCurrencies = async (searchText = "~ALL") => {
    try {
      const currencyRes = await axios.get(
        `${BASE_URL}/Currency/getallcurrency?searchText=${searchText}&pageNo=0&pageSize=1000`
      );
      const baseCurrencies = currencyRes.data.data
        .filter((currency) => currency.baseCurrency)
        .map((currency) => ({
          code: currency.code,
          name: currency.name,
        }));
      setBaseCurrencies(baseCurrencies);
      setFilteredBaseCurrencies(baseCurrencies); // Initialize filtered list

      if (baseCurrencies.length > 0 && !baseCurrencies.some((c) => c.code === selectedBaseCurrency.code)) {
        setSelectedBaseCurrency(baseCurrencies[0]);
      }
    } catch (error) {
      console.error("Error loading base currencies:", error);
      toast.error("Failed to load base currencies.");
    }
  };

  // Load exchange rates from backend API
  const loadData = async () => {
    if (!selectedBaseCurrency.code || isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const dateParam = selectedDate ? `&date=${formatApiDate(selectedDate)}` : "";
      const pageNo = pageState.skip / pageState.take;

      const response = await axios.get(
        `${BASE_URL}/api/ExchangeRate/getall?baseCurrencyCode=${selectedBaseCurrency.code}&searchText=${pageState.value}&pageNo=${pageNo}&pageSize=${pageState.take}${dateParam}`
      );

      if (response.data.data.length === 0) {
        toast.error(`No exchange rates found for ${formatApiDate(selectedDate)} and base currency ${selectedBaseCurrency.name}.`);
        if (!isResetting) {
          setIsResetting(true);
          setSelectedDate(initialDate);
          setSelectedBaseCurrency(initialBaseCurrency);
          setPageState((prev) => ({
            ...prev,
            skip: 0,
            value: "~ALL",
          }));
        }

      } else {
        setEntities(response.data.data);
        setPageState((prev) => ({
          ...prev,
          total: response.data.totalCount,
        }));
        setIsResetting(false);
      }
    } catch (error) {
      console.error("Error loading exchange rates:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized: Please log in again.");
      } else if (error.response?.status === 400) {
        toast.error("Bad request: Invalid parameters or date.");
      } else {
        toast.error("Failed to load exchange rates.");
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Debounced filter for ComboBox
  const filterBaseCurrencies = debounce(async (e) => {
    let searchText = "~ALL";
    if (e.filter && e.filter.value !== "") {
      searchText = e.filter.value;
    }
    await loadBaseCurrencies(searchText);
  }, 300);

  // Handle ComboBox change
  const handleBaseCurrencyChange = (e) => {
    const newValue = e.value || initialBaseCurrency;
    setSelectedBaseCurrency(newValue);
    setPageState((prev) => ({
      ...prev,
      skip: 0,
    }));
  };

  // Handle date change - reset pagination
  const handleDateChange = (e) => {
   
    setSelectedDate(e.value);
    setPageState((prev) => ({
      ...prev,
      skip: 0,
    }));
  };

  // Handle grid filter - reset pagination
  const onGridFilter = (e) => {
    let searchText = "~ALL";
    if (e.filter && e.filter.filters && e.filter.filters[0].value !== "") {
      searchText = e.filter.filters[0].value;
    }

    setPageState((prev) => ({
      ...prev,
      filter: { ...e.filter },
      skip: 0,
      value: searchText,
    }));
  };

  // Handle pagination change
  const handlePageChange = (e) => {
    setPageState((prev) => ({
      ...prev,
      skip: e.skip,
      take: e.take,
    }));
  };

  // Load base currencies on component mount
  useEffect(() => {
    loadBaseCurrencies();
  }, []);

  // Load data when key dependencies change
  useEffect(() => {
    if (selectedBaseCurrency.code && baseCurrencies.length > 0) {
      loadData();
    }
  }, [selectedBaseCurrency, selectedDate, pageState.value, pageState.skip, pageState.take, baseCurrencies.length]);

  return (
    <div className="px-4 py-4 sm:px-6 lg:px-8">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Exchange Rate</h3>
        <div className="flex gap-3">
          <div className="mt-3 sm:mt-0 sm:ml-4">
            <label htmlFor="baseCurrency" className="block text-sm font-medium text-gray-700">
              Base Currency
            </label>
            <div className="mt-1 relative" id="poCompo">
              <ComboBox
                data={filteredBaseCurrencies}
                textField="name"
                dataItemKey="code"
                name="baseCurrency"
                value={selectedBaseCurrency}
                onChange={handleBaseCurrencyChange}
                className="w-full h-8 rounded-md border-zinc-300 text-xs text-neutral-700"
                placeholder="Select Base Currency"
                suggest={true}
                clearButton={true}
                filterable={true}
                onFilterChange={filterBaseCurrencies}
              />
            </div>
          </div>
          <div>
            <label htmlFor="exchangeDate" className="block text-sm font-medium text-gray-700">
              Exchange Date
            </label>
            <div className="mt-1 flex h-8 border-t border-x rounded-md border-gray-300" id="poDateCompo">
              <DatePicker
                id="exchangeDate"
                value={selectedDate}
                onChange={handleDateChange}
                format="dd-MM-yyyy"
                className="mt-1 block w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                max={new Date()} // Prevent future dates
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow relative overflow-hidden sm:rounded-lg">
              {loading && <KendoLoader />}
              <BaseMasterGrid
                data={entities}
                columns={gridColumns}
                setPageState={setPageState}
                loadData={loadData}
                pageState={pageState}
                onGridFilter={onGridFilter}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default ExchangeRateMaster;